import {
    Component,
    ComponentRef,
    Input,
    OnInit,
    ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { ClaimCategory } from '../../../generic/classes/claim.category';
import { ClaimSolution } from '../../../generic/classes/claim.solution';
import { DynamicModal } from '../../../generic/component/modal/dynamic.modal.interface';
import { Logger } from '../../../generic/logging/default-log.service';
import { HttpClient } from '../../../generic/service/http.service';
import { ClaimService } from '../../../service/claim.service';
import { RouterLink } from '@angular/router';
import { FilePondModule } from 'ngx-filepond';
import { NgClass } from '@angular/common';

interface ClaimResponse {
    complaintId: string;
    errorMessage: string;
    errorStatus: number;
}

@Component({
    selector: 'new-claim-modal',
    templateUrl: 'new.claim.modal.component.html',
    styleUrls: ['./claim.component.scss'],
    standalone: true,
    imports: [
        NgClass,
        FormsModule,
        ReactiveFormsModule,
        FilePondModule,
        RouterLink,
    ],
})
export class NewClaimModalComponent implements OnInit, DynamicModal {
    SOLUTION_DIFFERENT_CODE = 'ANDERS';

    @Input() data: any;
    claimCategories: ClaimCategory[] = [];
    categoryReasons: ClaimCategory[] = [];
    claimSolutions: ClaimSolution[] = [];
    reloadShipmentLines: Subject<boolean> = new Subject();
    newClaimForm: UntypedFormGroup;
    claimSuccess: boolean = false;
    claimResponse: ClaimResponse;
    isErrorResponse: boolean = false;
    showQuantityField: boolean = false;
    showPicturesField: boolean = false;
    showDifferentSolutionField: boolean = false;
    picturesInputInvalid: boolean = false;
    popupOpen: boolean = false;
    _componentRef: ComponentRef<any>;

    uploadPhotoText = $localize`:@@claims.upload.photos:Drag and Drop your files or Browse`;

    @ViewChild('claimImagesUpload') claimImagesUpload: any;

    claimFilePondOptions = {
        className: 'claim-image-input',
        multiple: true,
        maxFiles: 10,
        allowImageTransform: true,
        imageTransformOutputQuality: 80,
        allowFileTypeValidation: true,
        acceptedFileTypes: ['image/*'],
        maxTotalFileSize: '100MB',
        labelIdle: this.uploadPhotoText,
        credits: false,
    };

    constructor(
        private http: HttpClient,
        private claimService: ClaimService,
        private logger: Logger,
        private formBuilder: UntypedFormBuilder,
    ) {
        this.popupOpen = true;

        this.claimService.getClaimCategories().subscribe(
            categories => (this.claimCategories = categories),
            error => this.logger.warn('Failed to get claim categories.', error),
        );

        this.claimService.getClaimSolutions().subscribe(
            solutions => (this.claimSolutions = solutions),
            error => this.logger.warn('Failed to get claim solutions.', error),
        );

        this.newClaimForm = formBuilder.group({
            type: ['', Validators.required],
            reason: ['', Validators.required],
            quantity: [''],
            comment: [''],
            solution: ['', Validators.required],
            solutionText: [''],
        });
    }

    ngOnInit(): void {
        this.onTypeChanges();
        this.onReasonChanges();
        this.onSolutionChanges();
    }

    close(): void {
        this.popupOpen = false;

        const htmlElement = document.querySelector('html');
        if (htmlElement?.classList.contains('scroll-lock')) {
            /*eslint no-eq-null: "error"*/
            htmlElement.classList.remove('scroll-lock');
        }

        setTimeout(() => {
            this._componentRef.destroy();
        }, 400);
    }

    getAmountDescription(): string {
        return this.data.boxQuantity + 'x' + this.data.quantityPerBox;
    }

    submitClaimButtonActive(): boolean {
        return this.newClaimForm.valid;
    }

    /**
     * Submit the information to create a new claim to the backend.
     *
     * @param value
     */
    onSubmit(value: any): boolean | Observable<unknown> {
        if (
            this.showPicturesField &&
            this.claimImagesUpload.getFiles().length < 1
        ) {
            this.picturesInputInvalid = true;
            return false;
        }

        if (this.submitClaimButtonActive()) {
            const $xhr = this.claimService.submitClaim(
                this.getClaimFormData(value),
            );
            $xhr.subscribe(
                (response: any) => {
                    this.claimResponse = response;
                    if (response.errorStatus === 0) {
                        this.reloadShipmentLines.next(true);
                        this.claimSuccess = true;
                        return;
                    }
                    this.isErrorResponse = true;
                },
                error => {
                    this.logger.error('Error submitting new claim', error);
                },
            );

            return $xhr;
        }

        return false;
    }

    /**
     * Creates a {@link FormData} object to be sent to the backend.
     *
     * @param value
     */
    getClaimFormData(value: any): FormData {
        const formData = new FormData();

        formData.append('claimCategory', value.type);
        formData.append('claimCode', value.reason);
        formData.append('orderId', this.data.orderId);
        formData.append('orderLineId', this.data.sourceLineNo);
        formData.append('claimSolutionCode', value.solution);

        if (value.quantity !== '') {
            formData.append('quantity', value.quantity);
        }

        if (value.comment !== '') {
            formData.append('description', value.comment);
        }

        if (
            this.showPicturesField &&
            this.claimImagesUpload.getFiles().length > 0
        ) {
            this.claimImagesUpload.getFiles().forEach(file => {
                formData.append('uploadedFiles', file.file);
            });
        }

        if (value.solutionText !== '') {
            formData.append('claimSolutionText', value.solutionText);
        }

        return formData;
    }

    /**
     * Changes the quantity to the max allowed on the blur event
     */
    changeQuantity(): void {
        const quantityValue = this.newClaimForm.get('quantity')?.value;
        const actualValue =
            quantityValue > this.data.amountOrdered
                ? this.data.amountOrdered
                : quantityValue;
        this.newClaimForm.get('quantity')?.setValue(actualValue);
    }

    getClaimTypes(): string[] {
        return [
            ...new Set(this.claimCategories.map(category => category.category)),
        ];
    }

    getClaimCategoryTitle(category: string): string | undefined {
        return this.claimCategories.find(
            claimCategory => category === claimCategory.category,
        )?.categoryDescription;
    }

    /**
     * Update and reset the reason dropdown select list anytime the type changes.
     * @private
     */
    private onTypeChanges(): void {
        this.newClaimForm.get('type')?.valueChanges.subscribe(chosenType => {
            this.categoryReasons = this.getListOfReasonsForType(chosenType);
            this.clearValidationReasonFields();
            this.newClaimForm.get('reason')?.setValue('');
        });
    }

    private clearValidationReasonFields(): void {
        this.clearValidationFromField('quantity');
    }

    private clearValidationFromField(fieldName: string): void {
        this.newClaimForm.get(fieldName)?.clearValidators();
        this.newClaimForm.get(fieldName)?.updateValueAndValidity();
    }

    /**
     * Changes the items in the reason select dropdown based on the chosen type.
     * Also, dynamically sets the 'required' validator on the 'quantity' input if that is required for the given reason
     *
     * @private
     */
    private onReasonChanges(): void {
        this.clearValidationReasonFields();
        this.newClaimForm
            .get('reason')
            ?.valueChanges.subscribe(chosenReason => {
                const chosenCategory = this.claimCategories.find(
                    category => category.code === chosenReason,
                );

                if (
                    chosenCategory &&
                    (chosenCategory.quantityRequired ||
                        chosenCategory.photoRequired)
                ) {
                    this.showQuantityField = chosenCategory.quantityRequired;
                    this.showPicturesField = chosenCategory.photoRequired;
                    this.newClaimForm
                        .get('quantity')
                        ?.setValidators([Validators.required]);
                    return;
                }

                this.showQuantityField = false;
                this.showPicturesField = false;
            });
    }

    private onSolutionChanges(): void {
        this.newClaimForm
            .get('solution')
            ?.valueChanges.subscribe(changedSolution => {
                const chosenSolution = this.claimSolutions.find(
                    solution => solution.code === changedSolution,
                );

                if (
                    chosenSolution &&
                    chosenSolution.code === this.SOLUTION_DIFFERENT_CODE
                ) {
                    this.showDifferentSolutionField = true;
                    this.newClaimForm
                        .get('solutionText')
                        ?.setValidators([Validators.required]);
                    return;
                }

                this.showDifferentSolutionField = false;
                this.clearValidationFromField('solutionText');
            });
    }

    /**
     * @param type
     * @private
     * @return ClaimCategory[] reasons for a chosen type
     */
    private getListOfReasonsForType(type: string): ClaimCategory[] {
        return this.claimCategories.filter(
            category => category.category === type,
        );
    }
}
